import type { Bible, ChangeOperation } from "../domain/types";
import type { WorldData } from "../domain/world";

export type RemoteState = {
  ok: boolean;
  revisionNumber: number;
  updatedAt: string;
  source: string;
  bible: Bible;
  world: WorldData;
  conflict?: boolean;
  currentRevision?: number;
  error?: string;
  errors?: Array<{ path: string; message: string }>;
};

async function parse(res: Response): Promise<RemoteState> {
  const data = (await res.json()) as RemoteState;
  if (!res.ok) {
    const err = new Error(data.error || `HTTP ${res.status}`) as Error & { payload?: RemoteState; status?: number };
    err.payload = data;
    err.status = res.status;
    throw err;
  }
  return data;
}

export async function fetchBible(): Promise<RemoteState> {
  const res = await fetch("/api/bible.php", { credentials: "same-origin" });
  return parse(res);
}

export async function saveBible(input: {
  bible: Bible;
  world?: WorldData;
  baseRevisionNumber: number;
  summary: string;
  operations?: ChangeOperation[];
}): Promise<RemoteState> {
  const res = await fetch("/api/save.php", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return parse(res);
}

export async function previewImport(bible: Bible): Promise<RemoteState & { preview?: boolean; counts?: unknown; currentCounts?: unknown }> {
  const res = await fetch("/api/import.php", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ bible, preview: true }),
  });
  return parse(res);
}

export async function applyImport(bible: Bible, baseRevisionNumber: number, world?: WorldData): Promise<RemoteState> {
  const res = await fetch("/api/import.php", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ bible, world, baseRevisionNumber, preview: false }),
  });
  return parse(res);
}

export async function resetSeed(): Promise<RemoteState> {
  const res = await fetch("/api/reset.php", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ confirm: "RESET" }),
  });
  return parse(res);
}

export async function fetchRevisions(): Promise<{ revisions: Array<{ revision_number: number; created_at: string; summary: string }> }> {
  const res = await fetch("/api/revisions.php", { credentials: "same-origin" });
  return res.json();
}

export async function createBackup(): Promise<void> {
  await fetch("/api/backup.php", { method: "POST", credentials: "same-origin" });
}

export function exportUrl(): string {
  return "/api/export.php";
}
